/**
 * Person Model
 *
 * Represents a colleague or team member with their current work status
 * and location context. Extends DelegateEmployee with real-time presence info.
 */
import { DelegateEmployee } from '../services/delegation.service';

/** Current work status of a person (where they are working from). */
export type WorkStatus = 'office' | 'inoffice' | 'home' | 'ooo' | 'elsewhere' | 'none';

/** A person in the organisation with live status and location context. */
export interface Person extends DelegateEmployee {
  /** Current work status (e.g. 'office', 'home', 'ooo') */
  status?: WorkStatus;
  department?: string;
  jobTitle?: string;
  /** ID of the space (desk/room) where the person is currently located */
  currentSpaceId?: string;
  /** Display name of the current space */
  currentSpaceName?: string;
  /** Floor name where the person is currently located */
  currentFloorName?: string;
}

/**
 * @deprecated Use `WORK_STATUS_TO_EN` + `localizeStatus()` from
 * `core/constants/status-labels.ts` so labels honour the active locale.
 * Retained here only for legacy callers; not regenerated as a source of truth.
 */
export const STATUS_LABELS: Record<WorkStatus, string> = {
  office: 'At their desk',
  inoffice: 'In the office',
  home: 'Working from home',
  ooo: 'Out of office',
  elsewhere: 'Working elsewhere',
  none: '',
};

/**
 * Bridge from internal `WorkStatus` code → canonical-English status string
 * recognised by `STATUS_KEY` in `core/constants/status-labels.ts`.
 *
 * Use with `localizeStatus()` to render translated labels:
 *   localizeStatus(WORK_STATUS_TO_EN[person.status], transloco)
 *
 * `none` returns '' (no label rendered) — matches the previous behaviour.
 */
export const WORK_STATUS_TO_EN: Record<WorkStatus, string> = {
  office: 'At their desk',
  inoffice: 'In the office',
  home: 'Working from home',
  ooo: 'Out of office',
  elsewhere: 'Working elsewhere',
  none: '',
};

/** Icon names (from ICON_REGISTRY) for each work status. */
export const STATUS_ICONS: Record<WorkStatus, string> = {
  office: 'desk',
  inoffice: 'building',
  home: 'home',
  ooo: 'close',
  elsewhere: 'globe',
  none: '',
};
